import { useRef, useLayoutEffect } from "react";
import QlikCard from "components/QlikCard";
import QlikObject from "components/QlikObject";

export default function TabOverview({ app, menuItemsStrings, ExportChartFootnote }) {
  // ── Equalize the two chart rows across every screen size ──────────────────
  // The top charts row shares band-top with variable-height content (the two
  // .overview-intro text blocks + the KPI row). charts-balanced sizes the bands
  // off --top-floor, which must equal that fixed content + --charts-floor. Since
  // the intro text rewraps at different widths, we can't hard-code it — instead
  // we measure the real height above the top charts row and write --top-floor,
  // so both rows come out equal (and never below --charts-floor / 280px).
  const rootRef = useRef(null);
  const topBandRef = useRef(null);
  const topChartsRef = useRef(null);
  const lastFixedRef = useRef(0);

  useLayoutEffect(() => {
    const root = rootRef.current;
    const band = topBandRef.current;
    const charts = topChartsRef.current;
    if (!root || !band || !charts) return;

    let raf = 0;
    const apply = () => {
      // distance from band-top's top edge to the charts row = height of
      // everything above the charts (both intro blocks + KPI row + gaps)
      const fixed = charts.getBoundingClientRect().top - band.getBoundingClientRect().top;
      if (fixed <= 0) return;                                   // tab hidden / not laid out
      if (Math.abs(fixed - lastFixedRef.current) < 0.5) return; // no real change; avoid RO loops
      lastFixedRef.current = fixed;
      root.style.setProperty("--top-floor", `calc(${fixed}px + var(--charts-floor))`);
    };
    const measure = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(apply);   // defer the write to dodge ResizeObserver loops
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(band);
    ro.observe(charts);
    window.addEventListener("resize", measure);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  return (
    <div
      ref={rootRef}
      className="container-fluid page-default kpi-row-triple charts-balanced overview-tab fillpage-body"
      style={{ "--top-floor": "calc(520px + var(--charts-floor))" }}
    >
      <div className="row justify-content-start fillpage-row">

        <div className="col-xs-12 col-sm-12 col-md-12 col-lg-10 col-xl-10 fillpage-col">

          {/* TOP band — KPIs (fixed) + strip (fixed) + charts (fill) */}
          <div className="band band-top" ref={topBandRef}>

            <div className="row justify-content-left band-fixed">
              <div className="col-12">
                <div className="overview-intro">
                  <h3 className="overview-intro-title">{menuItemsStrings.tabmetricsOverviewBlock1Title}</h3>
                  <div
                    className="overview-intro-text"
                    dangerouslySetInnerHTML={{ __html: menuItemsStrings.tabmetricsOverviewBlock1Description }}
                  />
                </div>
              </div>
            </div>

            {/* KPI row — fixed height */}
            <div className="row justify-content-left band-kpis">

              <div className="col-xs-12 col-sm-12 col-md-12 col-lg-4 col-xl-4">
                {/* KPI: national level */}
                <h3 className="kpi-title">{menuItemsStrings.tabMetricsOverviewNationalLevel}</h3>
                <QlikCard
                  id="FegXyLM"
                  height="var(--kpi-h)"
                  showHeader={false}
                  showMaximize={false}
                  showExport={false}
                />
              </div>

              <div className="col-xs-12 col-sm-12 col-md-12 col-lg-4 col-xl-4">
                {/* KPI: regional level */}
                <h3 className="kpi-title">{menuItemsStrings.tabMetricsOverviewRegionalLevel}</h3>
                <QlikCard
                  id="GRdTjE"
                  height="var(--kpi-h)"
                  showHeader={false}
                  showMaximize={false}
                  showExport={false}
                />
              </div>

              <div className="col-xs-12 col-sm-12 col-md-12 col-lg-4 col-xl-4">
                {/* KPI: local level */}
                <h3 className="kpi-title">{menuItemsStrings.tabMetricsOverviewLocalLevel}</h3>
                <QlikCard
                  id="kyDyp"
                  height="var(--kpi-h)"
                  showHeader={false}
                  showMaximize={false}
                  showExport={false}
                />
              </div>

            </div>


            <div className="row justify-content-left band-fixed">
              <div className="col-12">
                <div className="overview-intro">
                  <h3 className="overview-intro-title">{menuItemsStrings.tabmetricsOverviewBlock2Title}</h3>
                  <div
                    className="overview-intro-text"
                    dangerouslySetInnerHTML={{ __html: menuItemsStrings.tabmetricsOverviewBlock2Description }}
                  />
                  <div
                    className="overview-intro-text top-padding-8"
                    dangerouslySetInnerHTML={{ __html: menuItemsStrings.tabmetricsOverviewBlock2Description2 }}
                  />
                </div>
              </div>
            </div>

            {/* Charts row (top) — fills the rest of the top band */}
            <div className="row justify-content-left band-charts" ref={topChartsRef}>

              <div className="col-xs-12 col-sm-12 col-md-12 col-lg-12 col-xl-4 d-flex flex-column">
                <div className="row justify-content-center h-100">
                  <div className="col-12 d-flex flex-column">
                    {/* bar chart 1 */}
                    <QlikCard
                      id="nKruBYj"
                      height="100%"
                      showHeader={true}
                      showMaximize={false}
                      showExport={true}
                      showPdfExport
                      showImageExport={true}
                      exportImageWidth={1280}
                      exportImageHeight={720}
                      onBeforeExportHandler={async ({ vis, exportType }) => {
                        console.log(vis);
                        if (exportType in { pdf: true, image: true } && vis)
                          await vis.setOptions({ showTitles: true, footnote: `${ExportChartFootnote}` });
                      }}
                      onAfterExportHandler={async ({ vis, exportType }) => {
                        console.log('Export type: ', exportType);
                        if (exportType in { pdf: true, image: true })
                          vis.setOptions({ showTitles: false, footnote: "" });
                      }}
                      exportText={menuItemsStrings.exportText}
                      exportCompletedText={menuItemsStrings.exportCompletedText}
                      closeBtnText={menuItemsStrings.closeBtnText}
                      exportSelectObjectText={menuItemsStrings.exportSelectObjectText}
                      exportNoObjectText={menuItemsStrings.exportNoObjectText}
                      noSelections
                    />
                  </div>
                </div>
              </div>

              <div className="col-xs-12 col-sm-12 col-md-12 col-lg-12 col-xl-4 d-flex flex-column">
                <div className="row justify-content-center h-100">
                  <div className="col-12 d-flex flex-column">
                    {/* bar chart 2 */}
                    <QlikCard
                      id="SNCzUC"
                      height="100%"
                      showHeader={true}
                      showMaximize={false}
                      showExport={true}
                      showPdfExport
                      showImageExport={true}
                      exportImageWidth={1280}
                      exportImageHeight={720}
                      onBeforeExportHandler={async ({ vis, exportType }) => {
                        console.log(vis);
                        if (exportType in { pdf: true, image: true } && vis)
                          await vis.setOptions({ showTitles: true, footnote: `${ExportChartFootnote}` });
                      }}
                      onAfterExportHandler={async ({ vis, exportType }) => {
                        console.log('Export type: ', exportType);
                        if (exportType in { pdf: true, image: true })
                          vis.setOptions({ showTitles: false, footnote: "" });
                      }}
                      exportText={menuItemsStrings.exportText}
                      exportCompletedText={menuItemsStrings.exportCompletedText}
                      closeBtnText={menuItemsStrings.closeBtnText}
                      exportSelectObjectText={menuItemsStrings.exportSelectObjectText}
                      exportNoObjectText={menuItemsStrings.exportNoObjectText}
                      noSelections
                    />
                  </div>
                </div>
              </div>

              <div className="col-xs-12 col-sm-12 col-md-12 col-lg-12 col-xl-4 d-flex flex-column">
                <div className="row justify-content-center h-100">
                  <div className="col-12 d-flex flex-column">
                    {/* bar chart 3 */}
                    <QlikCard
                      id="ButWHjm"
                      height="100%"
                      showHeader={true}
                      showMaximize={false}
                      showExport={true}
                      showPdfExport
                      showImageExport={true}
                      exportImageWidth={1280}
                      exportImageHeight={720}
                      onBeforeExportHandler={async ({ vis, exportType }) => {
                        console.log(vis);
                        if (exportType in { pdf: true, image: true } && vis)
                          await vis.setOptions({ showTitles: true, footnote: `${ExportChartFootnote}` });
                      }}
                      onAfterExportHandler={async ({ vis, exportType }) => {
                        console.log('Export type: ', exportType);
                        if (exportType in { pdf: true, image: true })
                          vis.setOptions({ showTitles: false, footnote: "" });
                      }}
                      exportText={menuItemsStrings.exportText}
                      exportCompletedText={menuItemsStrings.exportCompletedText}
                      closeBtnText={menuItemsStrings.closeBtnText}
                      exportSelectObjectText={menuItemsStrings.exportSelectObjectText}
                      exportNoObjectText={menuItemsStrings.exportNoObjectText}
                      noSelections
                    />
                  </div>
                </div>
              </div>

            </div>

          </div>

          {/* BOTTOM band — second charts row (fills the band) */}
          <div className="band band-bottom">
            <div className="row justify-content-left band-charts">

              <div className="col-xs-12 col-sm-12 col-md-12 col-lg-12 col-xl-4 d-flex flex-column">
                <div className="row justify-content-center h-100">
                  <div className="col-12 d-flex flex-column">
                    {/* bar chart 4 */}
                    <QlikCard
                      id="mWPCBEg"
                      height="100%"
                      showHeader={true}
                      showMaximize={false}
                      showExport={true}
                      showPdfExport
                      showImageExport={true}
                      exportImageWidth={1280}
                      exportImageHeight={720}
                      onBeforeExportHandler={async ({ vis, exportType }) => {
                        console.log(vis);
                        if (exportType in { pdf: true, image: true } && vis)
                          await vis.setOptions({ showTitles: true, footnote: `${ExportChartFootnote}` });
                      }}
                      onAfterExportHandler={async ({ vis, exportType }) => {
                        console.log('Export type: ', exportType);
                        if (exportType in { pdf: true, image: true })
                          vis.setOptions({ showTitles: false, footnote: "" });
                      }}
                      exportText={menuItemsStrings.exportText}
                      exportCompletedText={menuItemsStrings.exportCompletedText}
                      closeBtnText={menuItemsStrings.closeBtnText}
                      exportSelectObjectText={menuItemsStrings.exportSelectObjectText}
                      exportNoObjectText={menuItemsStrings.exportNoObjectText}
                      noSelections
                    />
                  </div>
                </div>
              </div>

              <div className="col-xs-12 col-sm-12 col-md-12 col-lg-12 col-xl-4 d-flex flex-column">
                <div className="row justify-content-center h-100">
                  <div className="col-12 d-flex flex-column">
                    {/* bar chart 5 */}
                    <QlikCard
                      id="NqXer"
                      height="100%"
                      showHeader={true}
                      showMaximize={false}
                      showExport={true}
                      showPdfExport
                      showImageExport={true}
                      exportImageWidth={1280}
                      exportImageHeight={720}
                      onBeforeExportHandler={async ({ vis, exportType }) => {
                        console.log(vis);
                        if (exportType in { pdf: true, image: true } && vis)
                          await vis.setOptions({ showTitles: true, footnote: `${ExportChartFootnote}` });
                      }}
                      onAfterExportHandler={async ({ vis, exportType }) => {
                        console.log('Export type: ', exportType);
                        if (exportType in { pdf: true, image: true })
                          vis.setOptions({ showTitles: false, footnote: "" });
                      }}
                      exportText={menuItemsStrings.exportText}
                      exportCompletedText={menuItemsStrings.exportCompletedText}
                      closeBtnText={menuItemsStrings.closeBtnText}
                      exportSelectObjectText={menuItemsStrings.exportSelectObjectText}
                      exportNoObjectText={menuItemsStrings.exportNoObjectText}
                      noSelections
                    />
                  </div>
                </div>
              </div>

              <div className="col-xs-12 col-sm-12 col-md-12 col-lg-12 col-xl-4 d-flex flex-column">
                <div className="row justify-content-center h-100">
                  <div className="col-12 d-flex flex-column">
                    {/* bar chart 6 */}
                    <QlikCard
                      id="FbFhzc"
                      height="100%"
                      showHeader={true}
                      showMaximize={false}
                      showExport={true}
                      showPdfExport
                      showImageExport={true}
                      exportImageWidth={1280}
                      exportImageHeight={720}
                      onBeforeExportHandler={async ({ vis, exportType }) => {
                        console.log(vis);
                        if (exportType in { pdf: true, image: true } && vis)
                          await vis.setOptions({ showTitles: true, footnote: `${ExportChartFootnote}` });
                      }}
                      onAfterExportHandler={async ({ vis, exportType }) => {
                        console.log('Export type: ', exportType);
                        if (exportType in { pdf: true, image: true })
                          vis.setOptions({ showTitles: false, footnote: "" });
                      }}
                      exportText={menuItemsStrings.exportText}
                      exportCompletedText={menuItemsStrings.exportCompletedText}
                      closeBtnText={menuItemsStrings.closeBtnText}
                      exportSelectObjectText={menuItemsStrings.exportSelectObjectText}
                      exportNoObjectText={menuItemsStrings.exportNoObjectText}
                      noSelections
                    />
                  </div>
                </div>
              </div>

            </div>
          </div>

        </div>


        {/* Right column — single filter object, fills the whole column.
            (Hidden on tablet/mobile along with the other narrow right
            columns, via .fillpage-col.remove-header-spacing.) */}
        <div className="col-xs-12 col-sm-12 col-md-12 col-lg-2 col-xl-2 remove-header-spacing fillpage-col">
          <div className="filter-grow">
            <QlikCard
              id="pgtdtf"
              height="100%"
              showHeader={false}
              showMaximize={false}
              showExport={false}
              showOverlayer={false}
            />
          </div>
        </div>

      </div>
    </div>
  );
}